import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  XMarkIcon,
  UserGroupIcon,
  ArrowDownTrayIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';

export default function PouvoirsRecusModal({
  isOpen,
  onClose,
  reunionId,
  mandataire,
  isReadOnly,
  onPouvoirCancelled,
  onDownloadPdf,
}) {
  const [pouvoirs, setPouvoirs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [cancellingId, setCancellingId] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen || !mandataire?.userId) return;
    fetchPouvoirs();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, mandataire?.userId, reunionId]);

  const fetchPouvoirs = async () => {
    try {
      setLoading(true);
      setError('');
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/reunions/${reunionId}/pouvoirs-recus/${mandataire.userId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = response.data || [];
      setPouvoirs(data);
      return data;
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors du chargement des pouvoirs');
      return [];
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async (pouvoirId, mandantLabel) => {
    if (
      !window.confirm(
        `Annuler le pouvoir de ${mandantLabel} ?\n\nLe mandant repassera en statut « absent » et le PDF sera supprimé.`
      )
    ) {
      return;
    }

    setCancellingId(pouvoirId);
    setError('');
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/reunions/${reunionId}/pouvoirs/${pouvoirId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await onPouvoirCancelled?.();
      const remaining = await fetchPouvoirs();
      if (remaining.length === 0) {
        onClose();
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors de l\'annulation du pouvoir');
    } finally {
      setCancellingId(null);
    }
  };

  if (!isOpen || !mandataire) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 py-8">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={onClose} />

        <div className="relative bg-white rounded-xl shadow-xl w-full max-w-lg">
          <div className="flex items-center justify-between px-6 py-4 border-b">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <UserGroupIcon className="h-5 w-5 text-indigo-600" />
                Pouvoirs reçus
              </h3>
              <p className="text-sm text-gray-500 mt-0.5">
                {mandataire.prenom} {mandataire.nom}
              </p>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          <div className="px-6 py-5">
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                {error}
              </div>
            )}

            {loading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
              </div>
            ) : pouvoirs.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-6">Aucun pouvoir enregistré.</p>
            ) : (
              <ul className="space-y-3">
                {pouvoirs.map((p) => {
                  const mandantLabel = `${p.mandant?.prenom || ''} ${p.mandant?.nom || ''}`.trim();
                  return (
                    <li
                      key={p._id}
                      className="p-4 border border-gray-200 rounded-lg bg-gray-50/50"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-900">
                            Pouvoir de {mandantLabel || '—'}
                          </p>
                          {p.createdAt && (
                            <p className="text-xs text-gray-500 mt-0.5">
                              Enregistré le{' '}
                              {new Date(p.createdAt).toLocaleDateString('fr-FR', {
                                day: 'numeric',
                                month: 'long',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                              })}
                            </p>
                          )}
                          {p.pdf?.nom && (
                            <p className="text-xs text-gray-500 mt-0.5 truncate">{p.pdf.nom}</p>
                          )}
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          {p.pdf?.hasPdf && (
                            <button
                              type="button"
                              onClick={() => onDownloadPdf?.(p._id)}
                              className="p-2 text-indigo-600 hover:bg-indigo-50 rounded-lg"
                              title="Télécharger le PDF"
                            >
                              <ArrowDownTrayIcon className="h-5 w-5" />
                            </button>
                          )}
                          {!isReadOnly && (
                            <button
                              type="button"
                              onClick={() => handleCancel(p._id, mandantLabel)}
                              disabled={cancellingId === p._id}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-red-700 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 disabled:opacity-50"
                            >
                              <TrashIcon className="h-4 w-4" />
                              {cancellingId === p._id ? 'Annulation…' : 'Annuler le pouvoir'}
                            </button>
                          )}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          <div className="px-6 py-4 bg-gray-50 flex justify-end rounded-b-xl">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-700 hover:text-gray-900"
            >
              Fermer
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
